import { get, map } from "lodash-es";

import type { AddressContext } from "./types";
import type { JsonSchema7, UISchemaElement } from "@jsonforms/core";
import { BrandConfigKeys } from "@upmind-automation/types";

export function useSchemaDefinitions({
  regions,
  baseModel,
  countries,
  config
}: Partial<AddressContext>): JsonSchema7["definitions"] {
  const schema = {
    address: {
      type: "object",
      title: "Address",
      required: ["address1", "city", "postcode", "countryId"],
      properties: {
        address1: {
          type: "string",
          title: "Address"
        },

        address2: {
          type: ["string", "null"],
          title: ""
        },

        city: {
          type: "string",
          title: "City"
        },

        postcode: {
          type: "string",
          title: "Postcode"
        },

        regionId: {
          type: ["string", "null"],
          title: "Region",
          ...(regions?.length &&
            Array.isArray(regions) && {
              enum: map(regions, "id"),
              options: regions.map(region => ({
                label: region.name,
                value: region.id
              }))
            })
        },

        countryId: {
          type: "string",
          title: "Country",
          default: baseModel?.countryId,
          ...(countries?.length &&
            Array.isArray(countries) && {
              enum: map(countries, "id"),
              options: countries.map(country => ({
                label: country.name,
                value: country.id
              }))
            })
        }
      }
    }
  };

  // ensure we honor the brand config
  if (get(config, BrandConfigKeys.REQUIRE_REGION_IN_ADDRESS)) {
    schema.address.required.push("regionId");
  }

  return schema;
}

export function useSchema({
  id,
  baseModel,
  config,
  countries,
  regions
}: AddressContext): JsonSchema7 {
  const schema: JsonSchema7 = {
    type: "object",
    title: "Address",
    required: ["address"],
    definitions: useSchemaDefinitions({
      config,
      countries,
      regions,
      baseModel
    }),
    properties: {
      id: {
        type: ["string", "null"],
        title: "ID",
        description: "The AutoGenerated ID of this Address.",
        readOnly: true
      },

      name: {
        type: ["string", "null"],
        title: "Name",
        default: baseModel?.name
      },

      address: { $ref: "#/definitions/address" }

      // --- DEPRECATED
      // type: {
      //   type: "number",
      //   title: "Address Type",
      //   default: baseModel?.type,
      //   oneOf: !AddressTypes?.length
      //     ? undefined
      //     : map(AddressTypes, item => ({
      //         const: item.key,
      //         title: item.value
      //       }))
      // }
    }
  };

  // ensure that id we are editing an existing address, that we require the name
  if (id) {
    schema.required!.push("name");
  }

  return schema;
}

export function useUischemaDefinitions({
  id,
  countries,
  regions
}: Partial<AddressContext> = {}) {
  return {
    type: "Control",
    scope: "#/properties/address",
    i18n: "form.address",
    options: {
      autoFocus: true,
      autocomplete: "off",
      detail: {
        type: "VerticalLayout",
        elements: [
          {
            type: "Control",
            scope: "#/properties/countryId",
            i18n: "form.country",
            options: {
              placeholder: "Select a country…"
            }
          },
          // ---
          {
            type: id ? "VerticalLayout" : "address",
            i18n: "form.address",
            options: {
              fields: ["address1", "address2"],
              placeholder: "Start typing your address",
              autoFocus: true
            },
            elements: [
              {
                type: "Group",
                options: {
                  border: false
                },
                elements: [
                  {
                    type: "Control",
                    scope: "#/properties/address1",
                    i18n: "form.address1",
                    options: {
                      placeholder: "House name, apartment number etc.",
                      autocomplete: "address-line1",
                      autoFocus: true
                    }
                  },
                  {
                    type: "Control",
                    scope: "#/properties/address2",
                    i18n: "form.address2",
                    options: {
                      placeholder: "Road, street name etc.",
                      autocomplete: "address-line2"
                    }
                  }
                ]
              },
              {
                type: "Control",
                scope: "#/properties/city",
                i18n: "form.city",
                options: {
                  placeholder: "City, town etc.",
                  autocomplete: "address-level2"
                }
              },
              {
                type: "HorizontalLayout",
                elements: [
                  {
                    type: "Control",
                    scope: "#/properties/regionId",
                    i18n: "form.region",
                    options: {
                      placeholder: "Select a region…",
                      autocomplete: "address-level1"
                    }
                  },
                  {
                    type: "Control",
                    scope: "#/properties/postcode",
                    i18n: "form.postcode",
                    options: {
                      placeholder: "eg. 10011",
                      autocomplete: "postal-code"
                    }
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  };
}

export function useUischema({
  id,
  countries,
  regions
}: Partial<AddressContext> = {}): UISchemaElement {
  const schema = {
    type: "VerticalLayout",
    elements: [useUischemaDefinitions({ id, countries, regions })]
  };

  return schema as UISchemaElement;
}
